import { action, extendObservable, computed } from 'mobx';
import FetchService from '../../../../core/services/FetchService';
import {
  getColorByIndicatorAndValue,
  formatValueByIndicator,
  groupCompaniesByType,
  groupValuesByKey,
  getAverageByIndicator,
} from './utils';
import { get, round, toNumber, isNil } from 'lodash';
import moment from 'moment';

class CustomerSuccessKpiListService {
  fetchCurrent = new FetchService();
  fetchPrevious = new FetchService();

  constructor(indicator, globalRoundPrecision = 0, isInSeconds = false) {
    this.indicator = indicator;
    this.globalRoundPrecision = globalRoundPrecision;
    this.isInSeconds = isInSeconds;
    extendObservable(this, {
      current: [],
      previous: [],
      global: computed(() => ({
        current:
          this.current &&
          getAverageByIndicator(this.current, indicator, this.globalRoundPrecision) / (this.isInSeconds ? 3600 : 1),
        previous:
          this.previous &&
          getAverageByIndicator(this.previous, indicator, this.globalRoundPrecision) / (this.isInSeconds ? 3600 : 1),
      })),
      bySegment: computed(() =>
        groupValuesByKey(
          this.current.map((x) => ({ ...x, value: toNumber(x.value) })),
          this.previous.map((x) => ({ ...x, value: toNumber(x.value) })),
          'segment',
          indicator
        )
          .map((item) =>
            this.isInSeconds
              ? {
                  ...item,
                  current: round(item.current / 3600, 2),
                  previous: round(item.previous / 3600, 2),
                }
              : item
          )
          .filter((item) => item.id !== 'NA')
      ),
      byDistributor: computed(() =>
        groupValuesByKey(
          this.current.map((x) => ({ ...x, value: toNumber(x.value) })),
          this.previous.map((x) => ({ ...x, value: toNumber(x.value) })),
          'distributor'
        ).map((item) =>
          this.isInSeconds
            ? {
                ...item,
                current: round(item.current / 3600, 2),
                previous: round(item.previous / 3600, 2),
              }
            : item
        )
      ),
    });
  }

  getByCompanyId = (id) => {
    return {
      current: round(
        get(
          this.current.find((item) => item.company.id === id),
          'value'
        ) / (this.isInSeconds ? 3600 : 1),
        2
      ),
      previous: round(
        get(
          this.previous.find((item) => item.company.id === id),
          'value'
        ) / (this.isInSeconds ? 3600 : 1),
        2
      ),
    };
  };

  getCompaniesByType = (type, typeId) =>
    groupCompaniesByType(this.current, this.previous, type, typeId).map((item) =>
      this.isInSeconds
        ? {
            ...item,
            current: round(item.current / 3600, 2),
            previous: round(item.previous / 3600, 2),
          }
        : item
    );

  getSummaryByType = (type, typeId) => {
    const typeData = get(this, { segment: 'bySegment', distributor: 'byDistributor' }[type], []);
    if (typeData.length) {
      return typeData.find((data) => data.id === typeId);
    }
    return {};
  };

  getFormattedValue = (value) => formatValueByIndicator(this.indicator, value);

  init = action(() => {
    this.current = [];
    this.previous = [];
  });

  load = action(() => {
    if (!this.current.length && !this.previous.length) {
      this.fetchCurrent
        .fetch({
          url: `/kpis/customer-success`,
          query: {
            indicator: this.indicator,
            referenceMonth: moment().subtract(1, 'months').format('YYYY-MM'),
          },
        })
        .then(() => {
          if (this.fetchCurrent.data) {
            this.current = this.fetchCurrent.data.filter((x) => !isNil(x.value));
          } else {
            this.current = [];
          }
        });
      this.fetchPrevious
        .fetch({
          url: '/kpis/customer-success',
          query: {
            indicator: this.indicator,
            referenceMonth: moment().subtract(2, 'months').format('YYYY-MM'),
          },
        })
        .then(() => {
          if (this.fetchPrevious.data) {
            this.previous = this.fetchPrevious.data.filter((x) => !isNil(x.value));
          } else {
            this.previous = [];
          }
        });
    }
  });

  getFeedbackColor = (value) => {
    return getColorByIndicatorAndValue(this.indicator, value);
  };
}

export const PartnerAccessFrequencyListService = new CustomerSuccessKpiListService('PARTNER_ACCESS_FREQUENCY');
export const PartnerLicensePackageUsageRateListService = new CustomerSuccessKpiListService(
  'PARTNER_LICENSE_PACKAGE_USAGE',
  2
);
export const PartnerStudentVariationListService = new CustomerSuccessKpiListService('PARTNER_STUDENT_VARIATION', 2);
export const StudentAccessFrequencyListService = new CustomerSuccessKpiListService('STUDENT_ACCESS_FREQUENCY');
export const StudentChurnListService = new CustomerSuccessKpiListService('STUDENT_CHURN', 2);
export const StudentEngagementListService = new CustomerSuccessKpiListService('STUDENT_ENGAGEMENT', 2, true);
export const StudentStudyQualityListService = new CustomerSuccessKpiListService('STUDENT_STUDY_QUALITY', 2);
